import { faFileExport } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, CircularProgress, Tooltip, Typography } from '@mui/material';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { inject, observer } from 'mobx-react';
import Configuracao from '@/util/Configuracao';
import NumeroProcessoUtil from '@/util/NumeroProcessoUtil';
import SystemUtil from '@/util/SystemUtil';
import ReactComponent from '../ReactComponent';
import { ref } from './FerramentasListaProcessos';
import DataUtil from '@/util/DataUtil';
dayjs.extend(customParseFormat);

/** DTO para facilitar a manipulação dos dados a serem exportados */
class ParteAdvogados {
    constructor(codigoDaParte, denominacaoDaParte, nomeDaParte, advogados) {
        this.codigoDaParte = codigoDaParte;
        this.denominacaoDaParte = denominacaoDaParte;
        this.nomeDaParte = nomeDaParte;
        this.advogados = advogados;
    }
}

class ExportarProcessosParaArquivoCsv extends ReactComponent {
    /**
     * Indica se deve ou não adicionar os advogados com inscrição no arquivo a ser exportado.
     * @static
     * @readonly
     * @constant {boolean}
     */
    static ADICIONAR_ADVOGADOS = true;

    /**
     * Separador CSV para o arquivo a ser exportado.
     * @static
     * @readonly
     * @constant {string}
     */
    static SEPARADOR_CSV = ';';

    /** @desc Maior quantidade de partes com advogados em um único processo (importante para definir, dinamicamente, o número máximo de colunas no arquivo a ser exportado) */
    controleQtdColunas = { maximoPartesComAdvogados: 0 };

    /** @desc Dados a serem exportados. */
    dados;

    /** @desc Controle de geração do arquivo. */
    gerandoArquivo = false;

    constructor(props) {
        super(props);

        makeObservable(this, {
            controleQtdColunas: observable,
            dados: observable,
            gerandoArquivo: observable,
            handleClickExportarLista: action,
            nomeArquivoCsv: computed,
            gerarDadosExportacao: action,
            cabecalhos: computed,
            conteudoCsv: computed,
            temFiltroAtivo: computed
        });
    }

    handleClickExportarLista = () => {
        this.gerandoArquivo = true;
        this.dados = this.gerarDadosExportacao();
        SystemUtil.exportarAquivoCsv(this.nomeArquivoCsv, this.conteudoCsv);
        this.gerandoArquivo = false;
    };

    /**
     * Monta e retorna o nome do arquivo CSV.
     *
     * @returns {string} O nome do arquivo CSV.
     */
    get nomeArquivoCsv() {
        return this.sessaoStore?.montarNomeArquivoExportacao('processos');
    }

    /**
     * Formata a chave sequencial adicionando zeros à esquerda.
     *
     * @param {number} index O valor sequencial para identificar a chave.
     * @returns {string} Chave formatada com zeros à esquerda.
     */
    static formatarChaveParte(index) {
        return ('0' + index).slice(-2);
    }

    /**
     * Monta os dados para exportação.
     */
    gerarDadosExportacao() {
        let processosSelecionados = this.store.processosFiltrados;
        let index = 0;
        let partesAdvogados = null;
        let parteAdvogados = null;
        let totalMaximoPartesComAdvogados = 0;
        let processosExportados = processosSelecionados.map(function (processo) {
            index++;
            let quantidadeSustentacoesOraisProcesso = 0;
            let quantidadePartesComInscricoesProcesso = 0;
            partesAdvogados = new Map();

            if (processo?.pedidoSustentacaoOral?.pedidosDetalhados) {
                processo.pedidoSustentacaoOral.pedidosDetalhados.forEach((pedido) => {
                    if (pedido.advogados && pedido.advogados.length > 0) {
                        quantidadeSustentacoesOraisProcesso += pedido.advogados.length;

                        if (ExportarProcessosParaArquivoCsv.ADICIONAR_ADVOGADOS) {
                            let advogados = [];
                            pedido.advogados.forEach((adv) => {
                                advogados.push(adv.nome);
                            });

                            quantidadePartesComInscricoesProcesso++;
                            let codigoDaParte = ExportarProcessosParaArquivoCsv.formatarChaveParte(
                                quantidadePartesComInscricoesProcesso
                            );
                            parteAdvogados = new ParteAdvogados(
                                codigoDaParte,
                                pedido.denominacaoDaParte,
                                pedido.nomeDaParte,
                                advogados
                            );
                            partesAdvogados.set(codigoDaParte, parteAdvogados);
                        }
                    }
                });
            }

            if (ExportarProcessosParaArquivoCsv.ADICIONAR_ADVOGADOS && quantidadePartesComInscricoesProcesso > 0) {
                if (totalMaximoPartesComAdvogados < quantidadePartesComInscricoesProcesso) {
                    totalMaximoPartesComAdvogados = quantidadePartesComInscricoesProcesso;
                }
            }

            let linhaExportacao = {
                sequencial: index,
                sequencialPreferencia: processo.informacaoProcessoSalaSessao
                    ? processo.informacaoProcessoSalaSessao.sequenciaPreferencia
                    : '',
                sistemaTramitacaoProcessual: processo.processo.origem === Configuracao.ORIGEM_PJE ? 'PJe' : 'eSIJ',
                numeroCompleto:
                    processo.processo.origem === Configuracao.ORIGEM_PJE
                        ? NumeroProcessoUtil.formatarNumeroProcesso(processo.processo.numeroCompleto, true, true)
                        : processo.processo.numeroCompleto,
                relatorSigla: processo?.processo?.relator?.sigla || null,
                composicao: processo?.composicao
                    ? processo.composicao.map((comp) => comp.magistrado.sigla).join(', ')
                    : null,
                vistor:
                    Array.isArray(processo?.informacoesAdicionais?.RetornoVistaRegimental) &&
                    processo.informacoesAdicionais.RetornoVistaRegimental.length > 0
                        ? processo.informacoesAdicionais.RetornoVistaRegimental.map((rvr) => rvr.magistrado.sigla).join(
                              ', '
                          )
                        : null,
                quantidadePartesInscricoes: processo.pedidoSustentacaoOral?.pedidosDetalhados
                    ? processo.pedidoSustentacaoOral.pedidosDetalhados.length
                    : 0,
                quantidadeSustentacoesOrais: quantidadeSustentacoesOraisProcesso,
                ordemPregao: processo.informacoesAdicionais.OrdemPregao,
                resultadoJulgamento: processo.resultadoJulgamento?.descricao,
                dataHoraJulgamento: DataUtil.formatarDataHoraParaCsv(processo.dataJulgamento),
                anotacao: processo?.informacaoProcessoSalaSessao?.anotacao || null
            };

            if (ExportarProcessosParaArquivoCsv.ADICIONAR_ADVOGADOS) {
                // Adiciona chaves e valores para cada parte com inscrição de advogado, na linha a ser exportada
                partesAdvogados.forEach((parteAdvs) => {
                    let indexFormatado = parteAdvs.codigoDaParte;

                    let keyParte = 'parte' + indexFormatado;
                    linhaExportacao[keyParte] = parteAdvs.nomeDaParte ? parteAdvs.nomeDaParte.trim() : '';

                    let keyDenominacaoParte = 'denominacao_parte' + indexFormatado;
                    linhaExportacao[keyDenominacaoParte] = parteAdvs.denominacaoDaParte
                        ? parteAdvs.denominacaoDaParte.trim()
                        : '';

                    let keyAdvogados = 'advogados' + indexFormatado;
                    linhaExportacao[keyAdvogados] = parteAdvs.advogados.map((adv) => adv.trim()).join(', ');
                });
            }

            return linhaExportacao;
        });

        runInAction(() => {
            this.controleQtdColunas.maximoPartesComAdvogados = totalMaximoPartesComAdvogados;
        });

        return processosExportados;
    }

    /**
     * Monta os cabeçalhos do CSV.
     */
    get cabecalhos() {
        let cabecalhos = [];

        cabecalhos.push(
            { label: 'Seq', key: 'sequencial' },
            { label: 'Seq. Preferência', key: 'sequencialPreferencia' },
            { label: 'Sistema', key: 'sistemaTramitacaoProcessual' },
            { label: 'Número Processo', key: 'numeroCompleto' },
            { label: 'Relator', key: 'relatorSigla' },
            { label: 'Composição', key: 'composicao' },
            { label: 'Retorno Vista Reg.', key: 'vistor' },
            { label: 'Total de Partes com Inscrições', key: 'quantidadePartesInscricoes' },
            { label: 'Total de Inscrições', key: 'quantidadeSustentacoesOrais' },
            { label: 'Ordem Pregão', key: 'ordemPregao' },
            { label: 'Resultado Julgamento', key: 'resultadoJulgamento' },
            { label: 'Data/Hora Julgamento', key: 'dataHoraJulgamento' },
            { label: 'Anotação', key: 'anotacao' }
        );

        if (ExportarProcessosParaArquivoCsv.ADICIONAR_ADVOGADOS) {
            // Adiciona os cabeçalhos dinamicamente de acordo com o máximo de partes com inscrições para um único processo
            for (let i = 1; i <= this.controleQtdColunas.maximoPartesComAdvogados; i++) {
                let indexFormatado = ExportarProcessosParaArquivoCsv.formatarChaveParte(i);

                let labelParteFormatado = 'Parte ' + indexFormatado + ' com inscrições';
                let keyParte = 'parte' + indexFormatado;
                cabecalhos.push({
                    label: labelParteFormatado,
                    key: keyParte
                });

                let labelDenominacaoParte = 'Denominação da parte ' + indexFormatado;
                let keyDenominacaoParte = 'denominacao_parte' + indexFormatado;
                cabecalhos.push({
                    label: labelDenominacaoParte,
                    key: keyDenominacaoParte
                });

                let labelAdvogadosFormatado = 'Advogados da parte ' + indexFormatado;
                let keyAdvogados = 'advogados' + indexFormatado;
                cabecalhos.push({
                    label: labelAdvogadosFormatado,
                    key: keyAdvogados
                });
            }
        }

        return cabecalhos;
    }

    /**
     * @desc Monta o conteúdo do CSV.
     */
    get conteudoCsv() {
        const sep = ExportarProcessosParaArquivoCsv.SEPARADOR_CSV;
        const cabecalhoCSV = this.cabecalhos.map((header) => header.label).join(sep);
        const linhasCSV = this.dados.map((item) => this.cabecalhos.map((header) => item[header.key]).join(sep));
        return [cabecalhoCSV, ...linhasCSV].join('\n');
    }

    get temFiltroAtivo() {
        return Array.isArray(this.store.filtros?.filtrosComValor) && this.store.filtros.filtrosComValor.length > 0;
    }

    render() {
        if (this.gerandoArquivo) {
            return <CircularProgress size="24px" />;
        }

        const filtrados = this.temFiltroAtivo ? ' filtrados' : '';

        if (this.observableProps.botaoTexto) {
            return <Box onClick={this.handleClickExportarLista}>{`Processos${filtrados} para arquivo CSV`}</Box>;
        }

        return (
            <Tooltip
                onClick={this.handleClickExportarLista}
                title={<Typography>{`Exportar listas de processos${filtrados} para arquivo CSV`}</Typography>}
                placement="top-start"
                ref={ref}
            >
                <div
                    className="corBotao"
                    aria-label={`Exportar listas de processos${filtrados} para arquivo CSV`}
                >
                    <FontAwesomeIcon
                        icon={faFileExport}
                        style={{ cursor: 'pointer', width: '22px', height: '22px', marginRight: '10px' }}
                    />
                </div>
            </Tooltip>
        );
    }
}

export default inject('stores')(observer(ExportarProcessosParaArquivoCsv));
